import { cryptoCurrencyDataSource } from '@/constants/datasource'
import type { RunToDatabase } from '@/types'
import { setActionPrices } from '@0xtorch/core'
import { fiatCurrencies } from '@pkg/basic'
import {
  getSyncTimestamp,
  selectNullCryptoPriceActions,
  selectPortfolio,
  updateActionTransferPrices,
  upsertSyncTimestamp,
} from '@pkg/database-portfolio'

/** 以下の条件を満たす Action Transfer の price を再取得して、取得できた場合に更新する
 * - Action.timestamp が 56 時間以上前
 * - ActionTransfer.cryptoId coingecko listing されている coin id
 * - ActionTransfer.price が null
 */
export const syncActionPrices = async (params: {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
}) => {
  const { readFromDatabase, writeToDatabase } = params
  const key = 'action-price'

  const lastSynced = await readFromDatabase((database) =>
    getSyncTimestamp({ database, key }),
  )
  // 最終同期から 24 時間以上経過していない場合は更新しない
  const now = Date.now()
  if (now - lastSynced < 24 * 60 * 60 * 1000) {
    return
  }

  const portfolio = await readFromDatabase((database) =>
    selectPortfolio({ database }),
  )
  if (!portfolio) {
    throw new Error('Portfolio not found')
  }
  const fiat = fiatCurrencies.find((fiat) => fiat.id === portfolio.fiat)
  if (!fiat) {
    throw new Error('Fiat currency not found')
  }

  // 対象 Action 取得
  const before = now - 56 * 60 * 60 * 1000 // 56 時間前
  const actions = await readFromDatabase((database) =>
    selectNullCryptoPriceActions({
      before,
      database,
      fiats: [...fiatCurrencies],
    }),
  )

  if (actions.length === 0) {
    // 最終同期時間を更新
    await writeToDatabase((database) =>
      upsertSyncTimestamp({
        database,
        key,
        timestamp: now,
      }),
    )
    return
  }

  // 価格更新
  const pricedActions = await setActionPrices({
    dataSource: cryptoCurrencyDataSource,
    actions,
    fiat,
  })

  // DB 更新
  await writeToDatabase((database) =>
    updateActionTransferPrices({
      actions: [...pricedActions],
      database,
    }),
  )

  // 最終同期時間を更新
  await writeToDatabase((database) =>
    upsertSyncTimestamp({
      database,
      key,
      timestamp: now,
    }),
  )
}
